import {Box, Flex, HStack, Icon, Stack, Text} from "@chakra-ui/react";
import {ArrowUpTrayIcon, CheckCircleIcon, LinkIcon, SparklesIcon, XMarkIcon} from "@heroicons/react/16/solid";
import {Button} from "../../../../../components/ui/button";
import {Tag} from "../../../../../components/ui/tag";
import {Tooltip} from "../../../../../components/ui/tooltip";
import {
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from "../../../../../components/ui/drawer";
import {TestItem} from "../pages/testsData";

/**
 * A single evidence-file pill — used in-row, in the unmatched tray, and (read-only, `shared`) for
 * evidence inherited from the sample. Shared pills are styled differently and carry a tooltip so
 * it's clear they're managed on the sample, not this test.
 */
export const EvidenceFilePill = ({
  file,
  draggable,
  shared,
  removable,
}: {
  file: string;
  draggable?: boolean;
  shared?: boolean;
  /** Show an "x" to detach the file. Ignored for shared pills (managed on the sample). */
  removable?: boolean;
}) => {
  const pill = (
    <HStack
      as="span"
      gap={1.5}
      px={2}
      py={0.5}
      bg={shared ? "gray.100" : "blue.50"}
      borderWidth="1px"
      borderColor={shared ? "gray.200" : "blue.200"}
      rounded="md"
      cursor={shared ? "default" : draggable ? "grab" : "pointer"}
      flexShrink={0}
      _hover={shared ? undefined : {borderColor: "blue.300", bg: "blue.100"}}
    >
      {shared && (
        <Icon asChild boxSize={3.5} color="gray.500" flexShrink={0}>
          <LinkIcon />
        </Icon>
      )}
      <Text fontSize="xs" color={shared ? "gray.600" : "blue.700"} whiteSpace="nowrap">
        {file}
      </Text>
      {removable && !shared && (
        <Box
          as="button"
          display="inline-flex"
          color="blue.400"
          _hover={{color: "blue.700"}}
          aria-label="Remove"
          onClick={e => e.stopPropagation()}
        >
          <Icon asChild boxSize={3.5} flexShrink={0}>
            <XMarkIcon />
          </Icon>
        </Box>
      )}
    </HStack>
  );
  if (!shared) return pill;
  return <Tooltip content="Shared from sample">{pill}</Tooltip>;
};

/**
 * The evidence files attached to an item, as file pills. `shared` files (inherited from the sample)
 * render first and read-only; `files` are this item's own evidence (removable unless `readOnly`).
 * Shows "—" only when both empty.
 */
export const EvidenceFiles = ({files, shared, readOnly}: {files: string[]; shared?: string[]; readOnly?: boolean}) => {
  const sharedFiles = shared ?? [];
  if (sharedFiles.length === 0 && files.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400">
        —
      </Text>
    );
  }
  return (
    <Flex gap={1.5} wrap="wrap" w="fit-content" maxW="md">
      {sharedFiles.map((file, i) => (
        <EvidenceFilePill key={`shared-${i}`} file={file} shared />
      ))}
      {files.map((file, i) => (
        <EvidenceFilePill key={i} file={file} removable={!readOnly} />
      ))}
    </Flex>
  );
};

/** A per-item evidence dropzone — the upload target for attaching evidence to an item (mock). */
export const EvidenceDropzone = () => (
  <HStack
    w="fit-content"
    gap={2}
    px={3}
    py={1.5}
    border="1px dashed"
    borderColor="gray.300"
    rounded="md"
    color="gray.500"
    cursor="pointer"
    _hover={{borderColor: "blue.400", color: "blue.600", bg: "blue.50"}}
  >
    <Icon asChild boxSize={4}>
      <ArrowUpTrayIcon />
    </Icon>
    <Text fontSize="sm" whiteSpace="nowrap">
      Drop evidence
    </Text>
  </HStack>
);

/**
 * A single evidence drawer — drop a batch of evidence and it's auto-matched to the right items.
 * Complements the per-item droppers. (Mock; nothing is wired up.)
 */
export const EvidenceDrawer = ({
  open,
  onClose,
  items,
  rows,
  idKey,
}: {
  open: boolean;
  onClose: () => void;
  items: TestItem[];
  rows: Record<string, string>[];
  idKey: string;
}) => (
  <DrawerRoot open={open} onOpenChange={e => !e.open && onClose()} size="md">
    <DrawerContent>
      <DrawerHeader>
        <DrawerTitle>Add evidence</DrawerTitle>
      </DrawerHeader>
      <DrawerBody>
        <Stack gap={5}>
          {/* Bulk dropzone */}
          <Stack
            align="center"
            justify="center"
            gap={2}
            minH="40"
            p={6}
            border="1px dashed"
            borderColor="gray.300"
            rounded="lg"
            bg="gray.50"
            color="gray.500"
            cursor="pointer"
            textAlign="center"
            _hover={{borderColor: "blue.400", color: "blue.600", bg: "blue.50"}}
          >
            <Icon asChild boxSize={8}>
              <ArrowUpTrayIcon />
            </Icon>
            <Text fontWeight="500">Drag evidence here, or click to upload</Text>
            <HStack gap={1.5} color="purple.600">
              <Icon asChild boxSize={3.5}>
                <SparklesIcon />
              </Icon>
              <Text fontSize="xs" color="gray.500">
                Files are automatically matched to the right items
              </Text>
            </HStack>
          </Stack>

          {/* Auto-match status per item */}
          <Stack gap={2}>
            <Text fontSize="sm" fontWeight="600" color="gray.fg">
              Auto-matched
            </Text>
            <Stack
              gap={0}
              border="1px solid"
              borderColor="gray.200"
              rounded="md"
              overflow="hidden"
              css={{"& > *:not(:first-child)": {borderTopWidth: "1px", borderColor: "{colors.gray.100}"}}}
            >
              {rows.map((row, i) => {
                const count = items[i]?.evidenceCount ?? 0;
                return (
                  <HStack key={i} px={4} py={2} gap={3} justify="space-between">
                    <Text fontSize="sm" color="gray.fg" truncate>
                      {row[idKey]}
                    </Text>
                    {count > 0 ? (
                      <HStack gap={1} color="green.600" flexShrink={0}>
                        <Icon asChild boxSize={4}>
                          <CheckCircleIcon />
                        </Icon>
                        <Text fontSize="xs">{count} files</Text>
                      </HStack>
                    ) : (
                      <Tag size="sm" colorPalette="gray" variant="surface">
                        Unmatched
                      </Tag>
                    )}
                  </HStack>
                );
              })}
            </Stack>
          </Stack>
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        <Button colorPalette="blue" onClick={onClose}>
          Done
        </Button>
      </DrawerFooter>
    </DrawerContent>
  </DrawerRoot>
);

/**
 * The "Unmatched evidence" tray — uploaded files not yet matched to an item, shown above the table
 * so they're hard to miss. (Drag-to-match is not wired up.) Renders nothing when everything is
 * matched, so the tray only appears when there's something to action.
 */
export const UnmatchedEvidenceTray = ({files}: {files: string[]}) => {
  if (files.length === 0) return null;
  return (
    <Stack gap={2.5} px={6} py={4} borderBottom="1px solid" borderColor="gray.200" bg="gray.50">
      <HStack gap={2}>
        <Text fontSize="sm" fontWeight="600" color="gray.fg">
          Unmatched evidence
        </Text>
        <Tag size="sm" colorPalette="gray" variant="surface">
          {files.length}
        </Tag>
        <Box flex={1} />
        <Text fontSize="xs" color="gray.500">
          Drag a file onto a row to attach it to that item
        </Text>
      </HStack>
      <Flex gap={2} wrap="wrap">
        {files.map((file, i) => (
          <EvidenceFilePill key={i} file={file} draggable removable />
        ))}
      </Flex>
    </Stack>
  );
};
